/**
 * WebHID telephony (usage page 0x0B): Hook Switch 0x20, Phone Mute 0x2F, Flash 0x21 on input
 * reports; LED outputs Off-Hook 0x17, Mute 0x09, Ring 0x18 (LED page 0x08). Best effort: the
 * report layout comes from `device.collections`; Jabra and Poly need their vendor host software
 * for full control. The self-check page lists devices and verifies hook / mute / ring.
 */
export const TELEPHONY_PAGE = 0x0b;
export const LED_PAGE = 0x08;
export const USAGE_HOOK = 0x20;
export const USAGE_FLASH = 0x21;
export const USAGE_MUTE = 0x2f;
export const LED_OFF_HOOK = 0x17;
export const LED_MUTE = 0x09;
export const LED_RING = 0x18;

export interface HidEvents {
  onHook?(offHook: boolean): void;
  onMute?(muted: boolean): void;
  onFlash?(): void;
}

interface BitField {
  reportId: number;
  byte: number;
  bit: number;
}

interface Layout {
  hook?: BitField;
  mute?: BitField;
  flash?: BitField;
  leds: { reportId: number; bytes: number; offHook?: BitField; mute?: BitField; ring?: BitField };
}

/** walk the collections to find bit positions; exported for tests */
export function layoutFromCollections(collections: readonly HIDCollectionInfo[]): Layout {
  const layout: Layout = { leds: { reportId: 0, bytes: 0 } };
  const walk = (c: HIDCollectionInfo): void => {
    for (const rep of c.inputReports ?? []) {
      let bitPos = 0;
      for (const item of rep.items ?? []) {
        const size = item.reportSize ?? 1;
        const count = item.reportCount ?? 1;
        const usages = item.usages ?? [];
        if (item.usagePage === TELEPHONY_PAGE) {
          usages.forEach((u, i) => {
            const pos = bitPos + i * size;
            const f: BitField = { reportId: rep.reportId ?? 0, byte: Math.floor(pos / 8), bit: pos % 8 };
            if (u === USAGE_HOOK) layout.hook = f;
            if (u === USAGE_MUTE) layout.mute = f;
            if (u === USAGE_FLASH) layout.flash = f;
          });
        }
        bitPos += size * count;
      }
    }
    for (const rep of c.outputReports ?? []) {
      let bitPos = 0;
      for (const item of rep.items ?? []) {
        const size = item.reportSize ?? 1;
        const count = item.reportCount ?? 1;
        const usages = item.usages ?? [];
        if (item.usagePage === LED_PAGE) {
          usages.forEach((u, i) => {
            const pos = bitPos + i * size;
            const f: BitField = { reportId: rep.reportId ?? 0, byte: Math.floor(pos / 8), bit: pos % 8 };
            layout.leds.reportId = rep.reportId ?? 0;
            if (u === LED_OFF_HOOK) layout.leds.offHook = f;
            if (u === LED_MUTE) layout.leds.mute = f;
            if (u === LED_RING) layout.leds.ring = f;
          });
        }
        bitPos += size * count;
        layout.leds.bytes = Math.max(layout.leds.bytes, Math.ceil(bitPos / 8));
      }
    }
    for (const child of c.children ?? []) walk(child);
  };
  for (const c of collections) walk(c);
  return layout;
}

function bit(data: DataView, f: BitField): boolean {
  if (f.byte >= data.byteLength) return false;
  return ((data.getUint8(f.byte) >> f.bit) & 1) === 1;
}

export class HidHeadset {
  private readonly layout: Layout;
  private lastHook = false;
  private lastMute = false;
  private leds = { offHook: false, mute: false, ring: false };

  constructor(
    readonly device: HIDDevice,
    private readonly events: HidEvents,
  ) {
    this.layout = layoutFromCollections(device.collections);
  }

  get supportsHook(): boolean {
    return Boolean(this.layout.hook);
  }

  async open(): Promise<void> {
    if (!this.device.opened) await this.device.open();
    this.device.oninputreport = (ev) => this.onReport(ev);
  }

  async close(): Promise<void> {
    this.device.oninputreport = null;
    if (this.device.opened) await this.device.close();
  }

  /** exported for tests: feed a raw report */
  onReport(ev: { reportId: number; data: DataView }): void {
    const { hook, mute, flash } = this.layout;
    if (hook && hook.reportId === ev.reportId) {
      const v = bit(ev.data, hook);
      if (v !== this.lastHook) {
        this.lastHook = v;
        this.events.onHook?.(v);
      }
    }
    if (mute && mute.reportId === ev.reportId) {
      const v = bit(ev.data, mute);
      if (v !== this.lastMute) {
        this.lastMute = v;
        this.events.onMute?.(v);
      }
    }
    if (flash && flash.reportId === ev.reportId && bit(ev.data, flash)) this.events.onFlash?.();
  }

  async setLeds(p: Partial<{ offHook: boolean; mute: boolean; ring: boolean }>): Promise<void> {
    this.leds = { ...this.leds, ...p };
    const L = this.layout.leds;
    if (L.bytes === 0) return;
    const buf = new Uint8Array(L.bytes);
    const set = (f: BitField | undefined, on: boolean): void => {
      if (f && on && f.byte < buf.length) buf[f.byte] = (buf[f.byte] ?? 0) | (1 << f.bit);
    };
    set(L.offHook, this.leds.offHook);
    set(L.mute, this.leds.mute);
    set(L.ring, this.leds.ring);
    try {
      await this.device.sendReport(L.reportId, buf);
    } catch {
      /* vendor software may own the LEDs */
    }
  }
}

export async function listHidDevices(hid: HID | undefined = navigator.hid): Promise<HIDDevice[]> {
  if (!hid) return [];
  try {
    return await hid.getDevices();
  } catch {
    return [];
  }
}

export async function requestHeadset(hid: HID | undefined = navigator.hid): Promise<HIDDevice | null> {
  if (!hid) return null;
  try {
    const devices = await hid.requestDevice({ filters: [{ usagePage: TELEPHONY_PAGE }] });
    return devices[0] ?? null;
  } catch {
    return null;
  }
}
